import { useSearchParams } from "react-router";
import { Link } from "react-router";
import { useLeaderboard, useMe } from "../api/hooks";
import type { BoardRow } from "../api/types";
import { Card, Empty, QueryState, Select, cx } from "../components/ui";
import { chips, relative } from "../lib/format";

export function LeaderboardPage() {
  const [params, setParams] = useSearchParams();
  const season = params.get("season") ?? "all";
  const board = useLeaderboard(season);
  const me = useMe();
  const current = board.data?.currentSeason ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Leaderboard</h1>
        <Select className="w-auto" value={season} onChange={(e) => setParams(e.target.value === "all" ? {} : { season: e.target.value })} aria-label="Season">
          <option value="all">All time</option>
          {current > 0 && <option value="current">Season {current} (current)</option>}
          {Array.from({ length: Math.max(0, current - 1) }, (_, i) => current - 1 - i).map((n) => (
            <option key={n} value={String(n)}>Season {n}</option>
          ))}
        </Select>
      </div>
      <p className="text-sm text-muted">Ranked by profit: net worth minus every chip ever granted. Allowances don't count, only good calls.</p>

      <QueryState q={board}>
        {() => {
          const b = board.data!;
          if (b.rows.length === 0) return <Empty>No rankings yet. The board updates every 15 minutes.</Empty>;
          const mine = b.me && !b.rows.some((r) => r.userId === b.me!.userId) ? b.me : null;
          return (
            <Card className="p-0">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted">
                  <tr><th className="p-3">#</th><th className="p-3">Member</th><th className="p-3 text-right">Profit</th><th className="hidden p-3 text-right sm:table-cell">Net worth</th></tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {b.rows.map((r) => <Row key={r.userId} r={r} me={r.userId === me.data?.user.id} />)}
                  {mine && <Row r={mine} me />}
                </tbody>
              </table>
              {b.computedAt && <p className="p-3 text-xs text-muted">Updated {relative(b.computedAt)}</p>}
            </Card>
          );
        }}
      </QueryState>
    </div>
  );
}

function Row({ r, me }: { r: BoardRow; me: boolean }) {
  return (
    <tr className={cx(me && "bg-accent/10")}>
      <td className="p-3 text-muted">{r.rank}</td>
      <td className="p-3"><Link to={`/u/${r.username}`} className="font-medium hover:underline">{r.displayName}</Link> <span className="text-xs text-muted">@{r.username}</span></td>
      <td className={cx("p-3 text-right font-medium", r.profitM >= 0 ? "text-yes" : "text-no")}>{chips(r.profitM, { signed: true })}</td>
      <td className="hidden p-3 text-right text-muted sm:table-cell">{chips(r.netWorthM)}</td>
    </tr>
  );
}
